import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthProvider';
import { API_URL } from '../../utils/config';
import './AddOrganizerForm.css';

const AddOrganizerForm = () => {
  const { t } = useTranslation('organizers');
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  
  const [formData, setFormData] = useState({
    firstName: '',
    familyName: '',
    phone: '',
    email: '',
    password: '',
    role: 'viewer'
  });
  
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const baseUrl = API_URL;

  const getLangFromLocalStorage = () => {
    return localStorage.getItem('language') || 'en';
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // First Name validation
    if (!formData.firstName.trim()) {
      newErrors.firstName = t('firstNameRequired');
    } else if (formData.firstName.trim().length < 2) {
      newErrors.firstName = t('firstNameMinLength');
    } else if (!/^[a-zA-Z\s]+$/.test(formData.firstName.trim())) {
      newErrors.firstName = t('firstNameInvalidChars');
    }

    // Family Name validation
    if (!formData.familyName.trim()) {
      newErrors.familyName = t('familyNameRequired');
    } else if (formData.familyName.trim().length < 2) {
      newErrors.familyName = t('familyNameMinLength');
    } else if (!/^[a-zA-Z\s]+$/.test(formData.familyName.trim())) {
      newErrors.familyName = t('familyNameInvalidChars');
    }

    // Phone validation
    if (!formData.phone.trim()) {
      newErrors.phone = t('phoneRequired');
    } else if (!/^[\+]?[1-9][\d]{0,15}$/.test(formData.phone.trim().replace(/\s/g, ''))) {
      newErrors.phone = t('phoneInvalid');
    }

    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = t('emailRequired');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      newErrors.email = t('emailInvalid');
    }

    // Password validation
    if (!formData.password.trim()) {
      newErrors.password = t('passwordRequired');
    } else {
      const password = formData.password.trim();
      const passwordErrors = [];
      
      if (password.length < 8) {
        passwordErrors.push(t('passwordMinLength'));
      }
      if (!/[a-z]/.test(password)) {
        passwordErrors.push(t('passwordLowercase'));
      }
      if (!/[A-Z]/.test(password)) {
        passwordErrors.push(t('passwordUppercase'));
      }
      if (!/[0-9]/.test(password)) {
        passwordErrors.push(t('passwordNumber'));
      }
      if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
        passwordErrors.push(t('passwordSpecialChar'));
      }
      
      if (passwordErrors.length > 0) {
        newErrors.password = `${t('passwordMustContain')} ${passwordErrors.join(', ')}`;
      }
    }

    // Role validation
    if (!formData.role || !['viewer', 'manager'].includes(formData.role)) {
      newErrors.role = t('roleRequired');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    setIsLoading(true);
    setApiError('');
    
    try {
      const response = await fetch(`${baseUrl}/api/v1/attnd-supplier/organizer/create`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'lang': getLangFromLocalStorage(),
        },
        body: JSON.stringify({
          firstName: formData.firstName,
          familyName: formData.familyName,
          // name: `${formData.firstName} ${formData.familyName}`,
          phone: formData.phone,
          email: formData.email,
          password: formData.password,
          role: formData.role
        })
      });


      if (!response.ok) {
        const errorData = await response.json();
        
        // Handle structured field errors from API (for any error status)
        if (errorData.errors && Array.isArray(errorData.errors)) {
          const serverErrors = {};
          errorData.errors.forEach(error => {
            const fieldName = Object.keys(error)[0];
            const errorMessage = error[fieldName];
            serverErrors[fieldName] = errorMessage;
          });
          setErrors(serverErrors);
          
          // If we have field errors, also show a generic message if available
          if (errorData.error || errorData.message) {
            setApiError(errorData.error || errorData.message);
          }
          return; // Don't throw error, errors are already set in state
        }
        
        // Handle specific status codes
        if (response.status === 409) {
          const errorMessage = errorData.error || errorData.message || t('emailOrUsernameExists');
          setApiError(errorMessage);
          return;
        }
        
        // Generic error handling
        const errorMessage = errorData.error || errorData.message || `Failed to create organizer: ${response.status}`;
        setApiError(errorMessage);
        return;
      }

      const result = await response.json();
      console.log('Organizer created successfully:', result);
      
      // Show success state briefly before redirecting
      setIsSuccess(true);
      
      // Navigate to success page after short delay
      setTimeout(() => {
        navigate('/organizers/success');
      }, 1500);
      
    } catch (error) {
      console.error('Error creating organizer:', error);
      // Only set apiError if it's a network error or unexpected error
      // Field validation errors are already handled above
      if (error.message && !error.message.includes('Validation failed')) {
        setApiError(error.message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/organizers');
  };

  const handleBreadcrumbBack = () => {
    navigate('/organizers');
  };

  return (
    <div className="add-organizer-container">
      {/* Breadcrumb */}
      <div className="breadcrumb">
        <button 
          onClick={handleBreadcrumbBack}
          className="breadcrumb-link"
        >
          <img src="/arrow-left.svg" alt={t('back')} className="breadcrumb-icon" />
          {t('organizers')}
        </button>
      </div>

      {/* Title */}
      <div className="form-header">
        <h1 className="form-title">{t('addOrganizer')}</h1>
        {isSuccess && (
          <div className="success-message">
            <img src="/check-circle.svg" alt={t('success')} className="success-icon" />
            {t('organizerCreatedSuccess')}
          </div>
        )}
        {apiError && (
          <div className="api-error">
            {/* <img src="/alert-circle.svg" alt="Error" className="error-icon" /> */}
            {apiError}
          </div>
        )}
      </div>

      {/* Form */}
      <div className="form-container">
        <form onSubmit={handleSubmit} className="organizer-form">
          {/* First Name Field */}
          <div className="form-group">
            <label htmlFor="firstName" className="form-label">
              {t('firstName')} *
            </label>
            <input
              type="text"
              id="firstName"
              name="firstName"
              value={formData.firstName}
              onChange={handleInputChange}
              className={`form-input ${errors.firstName ? 'error' : ''}`}
              placeholder={t('enterFirstName')}
            />
            {errors.firstName && (
              <span className="error-message">{errors.firstName}</span>
            )}
          </div>

          {/* Family Name Field */}
          <div className="form-group">
            <label htmlFor="familyName" className="form-label">
              {t('familyName')} *
            </label>
            <input
              type="text"
              id="familyName"
              name="familyName"
              value={formData.familyName}
              onChange={handleInputChange}
              className={`form-input ${errors.familyName ? 'error' : ''}`}
              placeholder={t('enterFamilyName')}
            />
            {errors.familyName && (
              <span className="error-message">{errors.familyName}</span>
            )}
          </div>

          {/* Phone Field */}
          <div className="form-group">
            <label htmlFor="phone" className="form-label">
              {t('phone')} *
            </label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleInputChange}
              className={`form-input ${errors.phone ? 'error' : ''}`}
              placeholder={t('enterPhoneNumber')}
            />
            {errors.phone && (
              <span className="error-message">{errors.phone}</span>
            )}
          </div>

          {/* Email Field */}
          <div className="form-group">
            <label htmlFor="email" className="form-label">
              {t('email')} *
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              className={`form-input ${errors.email ? 'error' : ''}`}
              placeholder={t('enterEmailAddress')}
            />
            {errors.email && (
              <span className="error-message">{errors.email}</span>
            )}
          </div>

          {/* Password Field */}
          <div className="form-group">
            <label htmlFor="password" className="form-label">
              {t('password')} *
            </label>
            <div className="password-input-container">
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                className={`form-input ${errors.password ? 'error' : ''}`}
                placeholder={t('enterPassword')}
              />
              <button
                type="button"
                onClick={togglePasswordVisibility}
                className="password-toggle-btn"
                title={showPassword ? t('hidePassword') : t('showPassword')}
              >
                <img 
                  src={showPassword ? '/eye.svg' : '/eye-slash.svg'} 
                  alt={showPassword ? t('hidePassword') : t('showPassword')} 
                  className="password-eye-icon" 
                />
              </button>
            </div>
            {errors.password && (
              <span className="error-message">{errors.password}</span>
            )}
          </div>

          {/* Role Field */}
          <div className="form-group">
            <label htmlFor="role" className="form-label">
              {t('role')} *
            </label>
            <select
              id="role"
              name="role"
              value={formData.role}
              onChange={handleInputChange}
              className="form-select"
            >
              <option value="viewer">{t('viewer')}</option>
              <option value="manager">{t('manager')}</option>
            </select>
          </div>

          {/* Form Actions */}
          <div className="form-actions">
            <button
              type="button"
              onClick={handleCancel}
              className="cancel-btn"
              disabled={isLoading}
            >
              {t('cancel')}
            </button>
            <button
              type="submit"
              className={`add-btn ${isSuccess ? 'success' : ''}`}
              disabled={isLoading || isSuccess}
            >
              {isSuccess ? (
                <span className="success-state">
                  {/* <img src="/check.svg" alt={t('success')} className="check-icon" /> */}
                  {t('createdSuccessfully')}
                </span>
              ) : isLoading ? (
                t('creating')
              ) : (
                t('addOrganizer')
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddOrganizerForm;